import pick from "lodash/pick";
import { MultiToggle } from "PFComponents/multi_toggle";
import { useBooking } from "PFCore/hooks/queries";
import { useBookingTemplate } from "PFCore/hooks/queries/bookings/booking_templates/use_booking_template";
import { Controller, useFormContext } from "react-hook-form";

import css from "../booking_form.module.scss";
import { BookingFormData, BookingFormDataEditOrCloneMode, BookingFormMode } from "../booking_form.types";
import { isEditMode } from "../booking_form.utils";
import { BookingFormValues } from "../booking_form_provider";
import { BookingType } from "../booking_type";
import { mapBookings } from "./map_bookings";
import { useBookingTypeOptions } from "./use_booking_type_options";

type BookingRangeColumnProps = {
  initialData: BookingFormData;
  mode: BookingFormMode;
};

export const BookingTypeSelector = ({ initialData, mode }: BookingRangeColumnProps) => {
  const { control, setValue, getValues } = useFormContext<BookingFormValues>();

  const bookingId =
    mode === BookingFormMode.Create ? undefined : (initialData as BookingFormDataEditOrCloneMode).bookingId;
  const { data: initialBooking } = useBooking(bookingId, { enabled: !!bookingId });
  const { data: bookingTemplate } = useBookingTemplate(initialBooking?.booking_template_id, {
    enabled: !!initialBooking?.booking_template_id
  });

  const bookingTypeOptions = useBookingTypeOptions({ initialBooking, mode });

  const handleChange = (onChange: (value: BookingType) => void) => (value: BookingType) => {
    const { bookings } = getValues();
    if (isEditMode(mode)) {
      switch (value) {
        case BookingType.Single: {
          const bookingsWithId = mapBookings(bookings, { bookingTemplateId: undefined }, initialBooking);
          setValue("bookings", bookingsWithId);
          break;
        }
        case BookingType.Repeated: {
          const source = bookingTemplate
            ? {
                ...pick(bookingTemplate, "start_date", "end_date"),
                bookingTemplateId: bookingTemplate.id
              }
            : undefined;
          const bookingsWithBookingTemplateId = mapBookings(bookings, { id: undefined }, source);
          setValue("bookings", bookingsWithBookingTemplateId);
          break;
        }
      }
    }
    onChange(value);
  };

  return (
    <>
      {bookingTypeOptions.length > 1 && (
        <div className={css.section}>
          <Controller
            name="bookingType"
            control={control}
            render={({ field }) => (
              <MultiToggle<BookingType>
                options={bookingTypeOptions}
                defaultSelected={field.value}
                onChange={handleChange(field.onChange)}
              />
            )}
          />
        </div>
      )}
    </>
  );
};
